import { useState } from 'react';
import * as Yup from 'yup';
import { useAtom } from 'jotai';

import {
  CircularProgress,
  Box,
  FormControl,
  OutlinedInput,
  Select,
  MenuItem,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
} from '@mui/material';
import { Toaster } from 'react-hot-toast';
import { COLORS } from '../../../theme';
import { alpha } from '@mui/material/styles';

import { useEditStore } from '../../Query/ManageStoreQuery';
import { useAllMerchant } from '../../Query/MerchantQuery';
import { useAllCategory } from '../../Query/CategoryQuery';

import SelectStore from '../Store/AddStore';
import ImageUploadField from '../Utils/ImageUploadField';
import { Formik, Form, Field } from 'formik';
import { useThemeColors } from '../../../hooks/useThemeColors';
import { storeAtom } from '../ManageRestaurantTable';
import MerchantNameField from '../Store/MerchantNameField';
import CategoryIdField from '../Store/CategoryIdField';
import MyEditor from '../../Stateless/RichTextEditor';
import StorePhoneNumberField from '../Store/StorePhoneNumberField';
import LocationComponent from '../../Stateless/Location';
import { splitPhoneNumber } from '../../HelperFunctions/validationUtils';


// interface MerchantData {
//   id: string;
//   name: string;
// }

// interface CategoryData {
//   id: string;
//   nameEnglish: string;
// }

// interface Option {
//   value: string;
//   label: string;
// }

const validationSchema = Yup.object().shape({
  merchantId: Yup.string().required('Please select a merchant'),
  categoryId: Yup.array().of(Yup.string()).min(1, 'Please select a category'),
  storeName: Yup.string()
    .required('Please enter store name')
    .max(50, 'Restaurant name cannot exceed 50 characters')
    .test(
      'no-leading-trailing-spaces',
      'Store name cannot start or end with spaces',
      (value) => value?.trim() === value
    )
    .test(
      'not-only-spaces',
      'Store name cannot be only spaces',
      (value) => value?.trim().length > 0
    ),
  storeNameArabic: Yup.string()
    .required('Please enter store name in Arabic')
    .max(50, 'Restaurant name cannot exceed 50 characters')
    .test(
      'no-leading-trailing-spaces',
      'Store name cannot start or end with spaces',
      (value) => value?.trim() === value
    )
    .test(
      'not-only-spaces',
      'Store name cannot be only spaces',
      (value) => value?.trim().length > 0
    )
    .matches(
      /^[\u0600-\u06FF\s]+$/,
      'Store name must contain only Arabic letters and spaces'
    ),
  minimumOrderAmount: Yup.number()
    .typeError('Must be a number')
    .min(1, 'Must be at least 1')
    .required('Please enter minimum order amount'),
  canPreOrder: Yup.string().required('Please select pre-order option'),
  // storeType: Yup.string().required('Please select a Store Type'),
  restaurantType: Yup.array()
    .of(Yup.string())
    .min(1, 'Please select at least one restaurant type'),
  preferableTime: Yup.array()
    .of(Yup.string())
    .min(1, 'Please select at least one preferable time'),
  deliveryTime: Yup.string().when('deliveryAvailable', {
    is: (val: string) => val === 'Yes',
    then: (schema) =>
      schema
        .required('Please enter response time')
        .matches(/^\d+$/, 'Only numeric values allowed'),
    otherwise: (schema) => schema.notRequired(),
  }),
  aboutStore: Yup.string()
    .test(
      'has-content',
      'Please enter about store',
      value => {
        if (!value) return false;
        // Remove HTML tags and whitespace
        const text = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
        // Check for at least one letter or number
        return /[a-zA-Z0-9]/.test(text);
      }
    )
    .required('Please enter about store'),
  aboutStoreArabic: Yup.string()
    .test(
      'has-arabic-content',
      'Please enter valid Arabic content',
      value => {
        if (!value) return false;
        const text = value
          .replace(/<[^>]*>/g, '')      // Remove HTML tags
          .replace(/&nbsp;/g, '')       // Remove &nbsp;
          .trim();

        // Check for at least one Arabic character and only Arabic-compatible content
        const arabicRegex = /^[\u0600-\u06FF\s0-9،؛؟.,\-!()]+$/; // Includes Arabic letters, digits, common punctuation
        return arabicRegex.test(text);
      }
    )
    .required('Please enter about store in Arabic'),
  deliveryRadiusKM: Yup.string()
    .matches(/^[0-9]+$/, 'Only numeric values allowed')
    .required('Please enter delivery radius'),
  storePhoneNumber: Yup.string()
    .required('Please enter the mobile number')
    .matches(/^[0-9]+$/, 'Mobile number should contain only numbers')
    .test(
      'len',
      'Mobile number must be 9 digits or more',
      (val) => val?.length >= 9
    )
    .notOneOf(['0000000000'], 'Mobile number cannot be all zeroes'),
  storeLogo: Yup.mixed()
    // .required('Restaurant logo is required')
    .test('fileSize', 'Logo must be less than 500kb', (value: any) => {
      if (!value || typeof value === 'string') return true;
      return value.size <= 500000;
    })
    .test(
      'dimensions',
      'Logo must be between 300x300px and 500x500px',
      (value: any): Promise<boolean> => {
        if (!value || !(value instanceof File)) return Promise.resolve(true);

        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.src = e.target?.result as string;

            img.onload = () => {
              const { width, height } = img;
              if (
                width >= 300 &&
                height >= 300 &&
                width <= 500 &&
                height <= 500
              ) {
                resolve(true);
              } else {
                resolve(false);
              }
            };

            img.onerror = () => resolve(false);
          };
          reader.readAsDataURL(value);
        });
      }
    ),

  storeBanner: Yup.array()
    .min(1, 'At least one banner image is required')
    .max(6, 'Maximum 6 banner images allowed')
    .test(
      'fileSize',
      'Each banner must be less than 1MB',
      (value: any[] | undefined) => {
        if (!value || !Array.isArray(value)) return true;
        return value.every((file) => {
          if (typeof file === 'string') return true;
          return file.size <= 1 * 1024 * 1024;
        });
      }
    )
    .test(
      'dimensions',
      'Image dimensions must be between 1366×300 and 1500×500',
      async (value: any[] | undefined): Promise<boolean> => {
        if (!value || !Array.isArray(value)) return true;

        const dimensionChecks = await Promise.all(
          value.map(async (file) => {
            if (typeof file === 'string') return { isValid: true };
            if (!(file instanceof File)) return { isValid: true };

            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                  const { width, height } = img;
                  const isValid =
                    width >= 1366 &&
                    width <= 1500 &&
                    height >= 300 &&
                    height <= 500;
                  resolve({ isValid, width, height });
                };
                img.onerror = () => resolve({ isValid: false });
                img.src = event.target?.result as string;
              };
              reader.onerror = () => resolve({ isValid: false });
              try {
                reader.readAsDataURL(file);
              } catch (error) {
                console.error('Error reading file:', error);
                resolve({ isValid: false });
              }
            });
          })
        );

        const invalidDimensions = dimensionChecks
          .filter((check: any) => !check.isValid && check.width && check.height)
          .map((check: any) => `${check.width}×${check.height}`);

        if (invalidDimensions.length > 0) {
          return false;
        }

        return true;
      }
    ),
  workingHours: Yup.object({
    sunday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }).test(
      'valid-working-hours',
      '',
      function (value) {
        if (!value.isOpen) return true;
        if (!value.startTime || !value.endTime) return true;
        const start = value.startTime.replace(':00Z', '');
        const end = value.endTime.replace(':00Z', '');
        if (start === '00:00' && end === '00:00') {
          return this.createError({
            path: `${this.path}.startTime`,
            message: 'Start and end time cannot both be 00:00 when open',
          });
        }
        const startNum = timeToNumber(value.startTime);
        const endNum = timeToNumber(value.endTime);
        if (startNum === -1 || endNum === -1) return true;
        if (endNum <= startNum) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: 'End time must be greater than start time',
          });
        }
        return true;
      }
    ),
    monday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }).test(
      'valid-working-hours',
      '',
      function (value) {
        if (!value.isOpen) return true;
        if (!value.startTime || !value.endTime) return true;
        const start = value.startTime.replace(':00Z', '');
        const end = value.endTime.replace(':00Z', '');
        if (start === '00:00' && end === '00:00') {
          return this.createError({
            path: `${this.path}.startTime`,
            message: 'Start and end time cannot both be 00:00 when open',
          });
        }
        const startNum = timeToNumber(value.startTime);
        const endNum = timeToNumber(value.endTime);
        if (startNum === -1 || endNum === -1) return true;
        if (endNum <= startNum) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: 'End time must be greater than start time',
          });
        }
        return true;
      }
    ),
    tuesday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }).test(
      'valid-working-hours',
      '',
      function (value) {
        if (!value.isOpen) return true;
        if (!value.startTime || !value.endTime) return true;
        const start = value.startTime.replace(':00Z', '');
        const end = value.endTime.replace(':00Z', '');
        if (start === '00:00' && end === '00:00') {
          return this.createError({
            path: `${this.path}.startTime`,
            message: 'Start and end time cannot both be 00:00 when open',
          });
        }
        const startNum = timeToNumber(value.startTime);
        const endNum = timeToNumber(value.endTime);
        if (startNum === -1 || endNum === -1) return true;
        if (endNum <= startNum) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: 'End time must be greater than start time',
          });
        }
        return true;
      }
    ),
    wednesday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }).test(
      'valid-working-hours',
      '',
      function (value) {
        if (!value.isOpen) return true;
        if (!value.startTime || !value.endTime) return true;
        const start = value.startTime.replace(':00Z', '');
        const end = value.endTime.replace(':00Z', '');
        if (start === '00:00' && end === '00:00') {
          return this.createError({
            path: `${this.path}.startTime`,
            message: 'Start and end time cannot both be 00:00 when open',
          });
        }
        const startNum = timeToNumber(value.startTime);
        const endNum = timeToNumber(value.endTime);
        if (startNum === -1 || endNum === -1) return true;
        if (endNum <= startNum) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: 'End time must be greater than start time',
          });
        }
        return true;
      }
    ),
    thursday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }).test(
      'valid-working-hours',
      '',
      function (value) {
        if (!value.isOpen) return true;
        if (!value.startTime || !value.endTime) return true;
        const start = value.startTime.replace(':00Z', '');
        const end = value.endTime.replace(':00Z', '');
        if (start === '00:00' && end === '00:00') {
          return this.createError({
            path: `${this.path}.startTime`,
            message: 'Start and end time cannot both be 00:00 when open',
          });
        }
        const startNum = timeToNumber(value.startTime);
        const endNum = timeToNumber(value.endTime);
        if (startNum === -1 || endNum === -1) return true;
        if (endNum <= startNum) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: 'End time must be greater than start time',
          });
        }
        return true;
      }
    ),
    friday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }).test(
      'valid-working-hours',
      '',
      function (value) {
        if (!value.isOpen) return true;
        if (!value.startTime || !value.endTime) return true;
        const start = value.startTime.replace(':00Z', '');
        const end = value.endTime.replace(':00Z', '');
        if (start === '00:00' && end === '00:00') {
          return this.createError({
            path: `${this.path}.startTime`,
            message: 'Start and end time cannot both be 00:00 when open',
          });
        }
        const startNum = timeToNumber(value.startTime);
        const endNum = timeToNumber(value.endTime);
        if (startNum === -1 || endNum === -1) return true;
        if (endNum <= startNum) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: 'End time must be greater than start time',
          });
        }
        return true;
      }
    ),
    saturday: Yup.object().shape({
      isOpen: Yup.boolean(),
      startTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
      endTime: Yup.string().when('isOpen', {
        is: true,
        then: () => Yup.string().required('This field is required'),
      }),
    }).test(
      'valid-working-hours',
      '',
      function (value) {
        if (!value.isOpen) return true;
        if (!value.startTime || !value.endTime) return true;
        const start = value.startTime.replace(':00Z', '');
        const end = value.endTime.replace(':00Z', '');
        if (start === '00:00' && end === '00:00') {
          return this.createError({
            path: `${this.path}.startTime`,
            message: 'Start and end time cannot both be 00:00 when open',
          });
        }
        const startNum = timeToNumber(value.startTime);
        const endNum = timeToNumber(value.endTime);
        if (startNum === -1 || endNum === -1) return true;
        if (endNum <= startNum) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: 'End time must be greater than start time',
          });
        }
        return true;
      }
    ),
  }),


  itemAvailableTime: Yup.object()
    .shape({
      breakfast: Yup.object().shape({
        startTime: Yup.string().when('$preferableTime', {
          is: (arr: string[] | undefined) => Array.isArray(arr) && arr.includes('Breakfast'),
          then: (schema) => schema.required('This field is required'),
          otherwise: (schema) => schema,
        }),
        endTime: Yup.string()
          .test(
            'is-greater',
            'End time must be greater than start time',
            function (value) {
              const ctx = (this as any).options?.context;
              const selected: string[] = Array.isArray(ctx?.preferableTime) ? ctx.preferableTime : [];
              // If Breakfast is not selected, skip validation entirely
              if (!selected.includes('Breakfast')) return true;
              const { startTime } = this.parent;
              if (!startTime || !value) return true;
              return timeToNumber(value) > timeToNumber(startTime);
            }
          )
          .when('$preferableTime', {
            is: (arr: string[] | undefined) => Array.isArray(arr) && arr.includes('Breakfast'),
            then: (schema) => schema.required('This field is required'),
            otherwise: (schema) => schema,
          }),
      }),
      lunch: Yup.object().shape({
        startTime: Yup.string().when('$preferableTime', {
          is: (arr: string[] | undefined) => Array.isArray(arr) && arr.includes('Lunch'),
          then: (schema) => schema.required('This field is required'),
          otherwise: (schema) => schema,
        }),
        endTime: Yup.string()
          .test(
            'is-greater',
            'End time must be greater than start time',
            function (value) {
              const ctx = (this as any).options?.context;
              const selected: string[] = Array.isArray(ctx?.preferableTime) ? ctx.preferableTime : [];
              if (!selected.includes('Lunch')) return true;
              const { startTime } = this.parent;
              if (!startTime || !value) return true;
              return timeToNumber(value) > timeToNumber(startTime);
            }
          )
          .when('$preferableTime', {
          is: (arr: string[] | undefined) => Array.isArray(arr) && arr.includes('Lunch'),
          then: (schema) => schema.required('This field is required'),
          otherwise: (schema) => schema,
        }),
      }),
      dinner: Yup.object().shape({
        startTime: Yup.string().when('$preferableTime', {
          is: (arr: string[] | undefined) => Array.isArray(arr) && arr.includes('Dinner'),
          then: (schema) => schema.required('This field is required'),
          otherwise: (schema) => schema,
        }),
        endTime: Yup.string()
          .test(
            'is-greater',
            'End time must be greater than start time',
            function (value) {
              const ctx = (this as any).options?.context;
              const selected: string[] = Array.isArray(ctx?.preferableTime) ? ctx.preferableTime : [];
              if (!selected.includes('Dinner')) return true;
              const { startTime } = this.parent;
              if (!startTime || !value) return true;
              return timeToNumber(value) > timeToNumber(startTime);
            }
          )
          .when('$preferableTime', {
          is: (arr: string[] | undefined) => Array.isArray(arr) && arr.includes('Dinner'),
          then: (schema) => schema.required('This field is required'),
          otherwise: (schema) => schema,
        }),
      }),
    })
    .test(
      'no-overlap',
      'Meal time ranges should not overlap',
      function (value) {
        if (!value) return true;
        const ctx = (this as any).options?.context;
        const selected: string[] = Array.isArray(ctx?.preferableTime) ? ctx.preferableTime : [];
        const labelMap: Record<string, string> = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner' };
        const ranges = [] as { name: string; start: number; end: number }[];
        for (const [meal, times] of Object.entries(value)) {
          // Skip meals not selected
          if (!selected.includes(labelMap[meal])) continue;
          const start = timeToNumber(times?.startTime);
          const end = timeToNumber(times?.endTime);
          if (start === -1 || end === -1) continue;

          for (const existing of ranges) {
            if (start < existing.end && end > existing.start) {
              return this.createError({
                path: `itemAvailableTime.${meal}`,
                message: `${meal} time overlaps with ${existing.name}`,
              });
            }
          }
          ranges.push({ name: meal, start, end });
        }

        return true;
      }
    )
    .test(
      'sequential-order',
      'Each meal time must start at or after the previous meal ends (Breakfast → Lunch → Dinner)',
      function (value) {
        if (!value) return true;
        const ctx = (this as any).options?.context;
        const selectedSet = new Set<string>(Array.isArray(ctx?.preferableTime) ? ctx.preferableTime : []);
        const labelMap: Record<string, string> = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner' };
        const order = ['breakfast', 'lunch', 'dinner'] as const;
        type Meal = typeof order[number];
        for (let i = 1; i < order.length; i++) {
          const prev = value[order[i - 1] as Meal];
          const curr = value[order[i] as Meal];
          // If either meal in the pair is not selected, skip this check
          if (!selectedSet.has(labelMap[order[i - 1]]) || !selectedSet.has(labelMap[order[i]])) {
            continue;
          }
          if (
            prev && curr &&
            prev.endTime && curr.startTime &&
            timeToNumber(curr.startTime) < timeToNumber(prev.endTime)
          ) {
            return this.createError({
              path: `itemAvailableTime.${order[i]}.startTime`,
              message: `${order[i][0].toUpperCase() + order[i].slice(1)} must start at or after ${order[i - 1][0].toUpperCase() + order[i - 1].slice(1)} ends`,
            });
          }
        }
        return true;
      }
    ),
  deliveryTimeUnit: Yup.string().when('deliveryAvailable', {
    is: (val: string) => val === 'Yes',
    then: (schema) => schema.required('Please select time unit'),
    otherwise: (schema) => schema.notRequired(),
  }),
  storeAddress: Yup.string().required('Please enter store address'),
  latitude: Yup.string().required('Latitude is required'),
  longitude: Yup.string().required('Longitude is required'),
  deliveryFee: Yup.mixed().when('deliveryAvailable', {
    is: (val: string) => val === 'Yes',
    then: (_schema: any) =>
      Yup.object().shape({
        deliveryChargeSetting: Yup.object().shape({
          peakHourDeliveryFeeStatus: Yup.object().shape({
            type: Yup.string().notRequired(),
            data: Yup.object().when('type', {
              is: 'enable',
              then: () =>
                Yup.object().test(
                  'peak-hours-validation',
                  'Peak hour times are invalid',
                  function (
                    value:
                      | Record<
                        string,
                        { startTime: string; endTime: string; amount: number }[]
                      >
                      | undefined
                  ) {
                    if (!value) return true;
                    const days = [
                      'sunday',
                      'monday',
                      'tuesday',
                      'wednesday',
                      'thursday',
                      'friday',
                      'saturday',
                    ];
                    let hasError = false;

                    for (const day of days) {
                      const hours = value[day];
                      if (hours && hours.length > 0) {
                        for (const hour of hours) {
                          const isStartZero = !hour.startTime || hour.startTime === '00:00:00Z';
                          const isEndZero = !hour.endTime || hour.endTime === '00:00:00Z';
                          if (isStartZero && isEndZero) {
                            hasError = true;
                            break;
                          }
                        }
                      }
                      if (hasError) break;
                    }

                    if (hasError) {
                      return this.createError({
                        message:
                          'Please select a valid start and end time for all enabled peak hours.',
                      });
                    }

                    return true;
                  }
                ),
              otherwise: () => Yup.object().notRequired(),
            }),
          }),
        }),
      }),
    otherwise: (_schema: any) => Yup.mixed().notRequired(),
  }),
}).test(
  'pickup-or-delivery',
  'Either Self Pickup or Delivery Available must be Yes',
  function (value) {
    const hasSelfPickup = (value as any)?.hasSelfPickup;
    const deliveryAvailable = (value as any)?.deliveryAvailable;
    if (hasSelfPickup === 'No' && deliveryAvailable === 'No') {
      return this.createError({
        path: 'deliveryAvailable',
        message: 'Either Self Pickup or Delivery Available must be Yes',
      });
    }
    return true;
  }
);

const timeToNumber = (time: string | undefined): number => {
  if (!time) return -1; // invalid or unset times return -1
  return parseInt(time.replace(':00Z', '').replace(':', ''));
};

const generateTimeOptions = () => {
  const times = [];
  for (let hours = 0; hours < 24; hours++) {
    for (let minutes = 0; minutes < 60; minutes += 30) {
      const hour = hours.toString().padStart(2, '0');
      const minute = minutes.toString().padStart(2, '0');
      times.push(`${hour}:${minute}`);
    }
  }
  // Add the last minute of the day for completeness
  if (!times.includes('23:59')) times.push('23:59');
  return times;
};

// type AddRestaurantProps = {
//   onCancel: () => void;
//   storeType: string;
//   storeTypeOnEdit: ("GroceryStore" | "PharmacyStore" | "Restaurant")[];
// };

const EditRestaurant = ({ onCancel, storeType, isEdit }: { onCancel: () => void; storeType: string; isEdit?: boolean }) => {
  const { warning } = useThemeColors();
  const [storeId] = useAtom(storeAtom);
  // const countryCodes = [
  //   '+971', '+966', '+965', '+973', '+974', '+968', '+20', '+962', '+961', '+964', '+92'
  // ];
  // const fullNumber = storeId?.restaurantDetails?.storePhoneNumber || '';
  // const matchedCode = countryCodes.find(code => fullNumber.startsWith(code));

  const {countryCode, phone:phoneNumber} = splitPhoneNumber(storeId?.restaurantDetails?.storePhoneNumber)
  // const phoneNumber = countryCode ? phone.slice(countryCode.length) : '';
  const [selectedCountryCode, setSelectedCountryCode] = useState(countryCode || '+962');
  const { mutate: EditStore } = useEditStore();

  // Fetch merchant data
  const { data: merchantData } = useAllMerchant({
    initialData: { storeType: storeId ? storeId?.storeType : storeType },
  });

  const merchantOptions = merchantData?.map((item: any) => ({
    value: item.id,
    label: item.name,
  })) || [];

  // Fetch category data
  const { data: categoryData } = useAllCategory({
    initialData: {
      storeType: storeId ? storeId?.storeType : storeType,
    },
  });

  const categoryOptions = categoryData?.map((item: any) => ({
    value: item.id,
    label: item.nameEnglish,
  })) || [];

  const categoryIds = storeId?.restaurantDetails?.categoryId || [];

  console.log('Store ID:', storeId);

  const initialValues = {
    categoryId: storeId ? (Array.isArray(categoryIds) ? categoryIds : [categoryIds]) : [],
    categoryName: storeId ? storeId?.restaurantCategory : '',
    storeAddress: storeId ? storeId?.restaurantDetails?.storeAddress?.address : '',
    storeName: storeId ? storeId.nameEnglish : '',
    merchantId: storeId ? storeId.restaurantDetails.merchantId : '',
    merchantName: storeId ? storeId.merchantName : '',
    storeNameArabic: storeId ? storeId.nameArabic : '',
    minimumOrderAmount: storeId ? storeId.restaurantDetails.minOrderAmount.value : null,
    canPreOrder: storeId ? (storeId.restaurantDetails.canPreOrder ? 'Yes' : 'No') : '',
    IsHalal: storeId ? (storeId.restaurantDetails.isHalal ? 'Yes' : 'No') : '',
    restaurantType: storeId ? storeId.restaurantDetails.restaurantType.map((item: string) => item.toLowerCase()) : [],
    preferableTime: storeId ? storeId.restaurantDetails.preferableTime.map((item: string) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase()) : [],
    hasSelfPickup: storeId ? (storeId.restaurantDetails.hasSelfPickup ? 'Yes' : 'No') : '',
    deliveryAvailable: storeId ? (storeId.restaurantDetails.deliveryAvailable ? 'Yes' : 'No') : '',
    deliveryTime: storeId ? storeId.restaurantDetails.deliveryTime.value : '',
    deliveryTimeUnit:
      storeId &&
      storeId.restaurantDetails &&
      storeId.restaurantDetails.deliveryTime &&
      storeId.restaurantDetails.deliveryTime.format
        ? storeId.restaurantDetails.deliveryTime.format.charAt(0).toUpperCase() +
          storeId.restaurantDetails.deliveryTime.format.slice(1)
        : 'Minutes',
    additionalCulinaryRequest: storeId ? (storeId.restaurantDetails.additionalCulinaryRequest ? 'Yes' : 'No') : '',
    aboutStore: storeId ? storeId.restaurantDetails.aboutStore : '',
    aboutStoreArabic: storeId ? storeId.restaurantDetails.aboutStoreArabic : '',
    deliveryRadiusKM: storeId ? storeId.restaurantDetails.deliveryRadiusKM : '',
    storePhoneNumber: phoneNumber,
    storeLogo: storeId ? storeId.restaurantDetails.storeLogo : null,
    storeBanner: storeId ? storeId.restaurantDetails.storeBanner : [],
    latitude: storeId ? storeId.restaurantDetails?.storeAddress?.latitude : '',
    longitude: storeId ? storeId.restaurantDetails?.storeAddress?.longitude : '',
    location: storeId ? {
      address: storeId?.restaurantDetails?.storeAddress.address,
      latitude: storeId?.restaurantDetails?.storeAddress.latitude,
      longitude: storeId?.restaurantDetails?.storeAddress.longitude,
    } : {
      address: '',
      latitude: '',
      longitude: '',
    },
    deliveryFee: storeId?.restaurantDetails?.deliveryFee || {
      deliveryChargeSetting: {
        type: 'default',
        deliveryFeeStatus: {
          type: 'disable',
          commonDeliveryFee: { type: 'disable', commonDeliveryFeeValue: 0 },
          feePerKM: { type: 'disable', feePerKMValue: 0 },
        },
        peakHourDeliveryFeeStatus: {
          type: 'disable',
          data: {
            sunday: [],
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
          },
        },
      },
    },
    itemAvailableTime: {
      breakfast: storeId?.restaurantDetails?.itemAvailableTime?.breakfast || { startTime: '', endTime: '' },
      lunch: storeId?.restaurantDetails?.itemAvailableTime?.lunch || { startTime: '', endTime: '' },
      dinner: storeId?.restaurantDetails?.itemAvailableTime?.dinner || { startTime: '', endTime: '' },
    },
    workingHours: {
      sunday: storeId?.restaurantDetails?.workingHours?.sunday || { isOpen: true, startTime: '', endTime: '' },
      monday: storeId?.restaurantDetails?.workingHours?.monday || { isOpen: true, startTime: '', endTime: '' },
      tuesday: storeId?.restaurantDetails?.workingHours?.tuesday || { isOpen: true, startTime: '', endTime: '' },
      wednesday: storeId?.restaurantDetails?.workingHours?.wednesday || { isOpen: true, startTime: '', endTime: '' },
      thursday: storeId?.restaurantDetails?.workingHours?.thursday || { isOpen: true, startTime: '', endTime: '' },
      friday: storeId?.restaurantDetails?.workingHours?.friday || { isOpen: true, startTime: '', endTime: '' },
      saturday: storeId?.restaurantDetails?.workingHours?.saturday || { isOpen: true, startTime: '', endTime: '' },
    },
  };

  const handleSubmit = async (values: typeof initialValues, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    try {
      console.log('EditRestaurant submit - raw values:', values);
      // Sanitize delivery fee fields: coerce numeric strings to numbers and default empty values to 0
      const sanitizeDeliveryFee = (deliveryFee: any) => {
        if (!deliveryFee || typeof deliveryFee !== 'object') return deliveryFee;
        const cloned = JSON.parse(JSON.stringify(deliveryFee));
        const status = cloned?.deliveryChargeSetting?.deliveryFeeStatus;
        if (status) {
          if (status.commonDeliveryFee) {
            const raw = status.commonDeliveryFee.commonDeliveryFeeValue;
            status.commonDeliveryFee.commonDeliveryFeeValue = raw === '' || raw == null ? 0 : +raw;
          }
          if (status.feePerKM) {
            const raw = status.feePerKM.feePerKMValue;
            status.feePerKM.feePerKMValue = raw === '' || raw == null ? 0 : +raw;
          }
        }
        const peak = cloned?.deliveryChargeSetting?.peakHourDeliveryFeeStatus?.data;
        if (peak && typeof peak === 'object') {
          const days = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
          days.forEach((d) => {
            if (Array.isArray(peak[d])) {
              peak[d] = peak[d].map((entry: any) => ({
                ...entry,
                amount: entry?.amount === '' || entry?.amount == null ? 0 : +entry.amount,
              }));
            }
          });
        }
        return cloned;
      };
      const formattedData: any = {
        ...values,
        storePhoneNumber: `${selectedCountryCode}${values.storePhoneNumber}`,
        storeName: values.storeName,
        storeNameArabic: values.storeNameArabic,
        minOrderAmount: {
          currency: 'JOD',
          value: +values.minimumOrderAmount,
        },
        canPreOrder: values.canPreOrder === 'Yes',
        isHalal: values.IsHalal === 'Yes',
        restaurantType: values.restaurantType,
        preferableTime: values.preferableTime.map((item: string) => item.toLowerCase()),
        storeType: storeType,
        hasSelfPickup: values.hasSelfPickup === 'Yes',
        deliveryAvailable: values.deliveryAvailable === 'Yes',
        additionalCulinaryRequest: values.additionalCulinaryRequest === 'Yes',
        aboutStore: values.aboutStore,
        aboutStoreArabic: values.aboutStoreArabic,
        storeAddress: {
          address: values.storeAddress || '',
          latitude: values.latitude || '',
          longitude: values.longitude || '',
        },
        workingHours: values.workingHours,
        itemAvailableTime: (() => {
          const mealLabelMap: Record<string, string> = {
            breakfast: 'Breakfast',
            lunch: 'Lunch',
            dinner: 'Dinner',
          };
          const preferredSet = new Set(values.preferableTime);
          const finalTimes = (['breakfast', 'lunch', 'dinner'] as const).reduce((acc, key) => {
            const label = mealLabelMap[key];
            const selected = preferredSet.has(label);
            if (!selected) {
              acc[key] = { startTime: '00:00:00Z', endTime: '00:00:00Z' };
            } else {
              acc[key] = {
                startTime: values.itemAvailableTime[key].startTime || '00:00:00Z',
                endTime: values.itemAvailableTime[key].endTime || '00:00:00Z',
              };
            }
            return acc;
          }, {} as typeof values.itemAvailableTime);
          return finalTimes;
        })(),
        deliveryRadiusKM: values.deliveryRadiusKM,
        storeLogo: values.storeLogo,
        storeBanner: values.storeBanner,
      };
      // Conditionally include/omit delivery fields
      if (values.deliveryAvailable === 'Yes') {
        formattedData.deliveryFee = sanitizeDeliveryFee(values.deliveryFee);
        formattedData.deliveryTime = {
          value: +values.deliveryTime,
          format: values?.deliveryTimeUnit?.toLowerCase(),
        };
      } else {
        delete formattedData.deliveryTime;
        delete formattedData.deliveryFee;
      }
      console.log('EditRestaurant submit - payload:', formattedData);
      EditStore(
        {
          id: storeId.id,
          data: formattedData,
        },
        {
          onSuccess: () => {
            onCancel();
          },
          onError: (error) => {
            console.error('EditStore error:', error);
          },
        }
      );
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const logAndValidate = async (values: typeof initialValues) => {
    try {
      // Use Yup's validation schema to validate
      await validationSchema.validate(values, { abortEarly: false, context: { preferableTime: values.preferableTime } });
      return {};
    } catch (err: any) {
      if (err && err.inner) {
        const errorMap: Record<string, string> = {};
        err.inner.forEach((validationError: any) => {
          if (validationError.path && !errorMap[validationError.path]) {
            errorMap[validationError.path] = validationError.message;
          }
        });
        // Log all errors to the console
        console.error('Formik Validation Errors:', errorMap);
        return errorMap;
      }
      // Log any other error
      console.error('Formik Validation Error:', err);
      return {};
    }
  };

  return (
    <>
      <Toaster position="top-right" />
      <SelectStore
        onCancel={onCancel}
        storeType={storeId?.storeType || storeType}
        isEdit={isEdit}
      />
      <Box
        maxWidth={800}
        mx="auto"
        my={4}
        p={{ xs: 2, sm: 4 }}
        bgcolor="#fff"
        borderRadius={3}
        boxShadow={2}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
          validate={logAndValidate}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue, setFieldTouched, submitCount }) => {

            console.log('Formik values:', values);
            console.log('Formik errors:', errors);
            return (
              <Form>
                {/* Merchant Name */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Merchant Name <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <Field name="merchantId" component={MerchantNameField} merchantOptions={merchantOptions} />
                  </Box>
                </Box>
                {/* Category */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Category <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <Field name="categoryId" component={CategoryIdField} storeId={storeId} categoryOptions={categoryOptions} />
                  </Box>
                </Box>
                {/* Store Name */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Store Name <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <FormControl error={touched.storeName && Boolean(errors.storeName)} fullWidth>
                      <OutlinedInput
                        name="storeName"
                        value={values.storeName}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          if (newValue.length <= 50) {
                            setFieldValue('storeName', newValue);
                            setFieldTouched('storeName', true, false);
                          } else {
                            setFieldValue('storeName', newValue.slice(0, 50));
                          }
                        }}
                        onBlur={() => setFieldTouched('storeName', true)}
                        placeholder="Enter Restaurant Name"
                        sx={{ width: { xs: '100%', sm: '400px' } }}
                      />
                    </FormControl>
                    {touched.storeName && typeof errors.storeName === 'string' && (
                      <Typography variant="caption" color="error">{errors.storeName}</Typography>
                    )}
                  </Box>
                </Box>
                {/* Store Name Arabic */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Store Name (Arabic) <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <FormControl error={touched.storeNameArabic && Boolean(errors.storeNameArabic)} fullWidth>
                      <OutlinedInput
                        name="storeNameArabic"
                        value={values.storeNameArabic}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          if (newValue.length <= 50) {
                            setFieldValue('storeNameArabic', newValue);
                            setFieldTouched('storeNameArabic', true, false);
                          } else {
                            setFieldValue('storeNameArabic', newValue.slice(0, 50));
                          }
                        }}
                        onBlur={() => setFieldTouched('storeNameArabic', true)}
                        placeholder="Enter Restaurant Name in Arabic"
                        sx={{ width: { xs: '100%', sm: '400px' } }}
                      />
                    </FormControl>
                    {touched.storeNameArabic && typeof errors.storeNameArabic === 'string' && (
                      <Typography variant="caption" color="error">{errors.storeNameArabic}</Typography>
                    )}
                  </Box>
                </Box>
                {/* Minimum Order Amount */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Minimum Order Amount <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <FormControl error={touched.minimumOrderAmount && Boolean(errors.minimumOrderAmount)} fullWidth>
                      <Box sx={{ display: 'flex', gap: '4px', width: { xs: '100%', sm: '400px' } }}>
                        <Box sx={{ width: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>JOD</Box>
                        <OutlinedInput
                          type="number"
                          value={values.minimumOrderAmount}
                          onChange={(e) => {
                            setFieldValue('minimumOrderAmount', e.target.value);
                            setFieldTouched('minimumOrderAmount', true, false);
                          }}
                          placeholder="Enter Minimum Order Amount"
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    </FormControl>
                    {touched.minimumOrderAmount && typeof errors.minimumOrderAmount === 'string' && (
                      <Typography variant="caption" color="error">{errors.minimumOrderAmount}</Typography>
                    )}
                  </Box>
                </Box>
                {/* Pre-order Option */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Pre-order Option <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <FormControl error={touched.canPreOrder && Boolean(errors.canPreOrder)} fullWidth>
                      <RadioGroup
                        row
                        name="canPreOrder"
                        value={values.canPreOrder}
                        onChange={(e) => setFieldValue('canPreOrder', e.target.value)}
                      >
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>
                    {touched.canPreOrder && typeof errors.canPreOrder === 'string' && (
                      <Typography variant="caption" color="error">{errors.canPreOrder}</Typography>
                    )}
                  </Box>
                </Box>
                {/* Halal */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Halal </Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <FormControl error={touched.IsHalal && Boolean(errors.IsHalal)} fullWidth>
                      <RadioGroup
                        row
                        name="IsHalal"
                        value={values.IsHalal}
                        onChange={(e) => setFieldValue('IsHalal', e.target.value)}
                      >
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                      {touched.IsHalal && typeof errors.IsHalal === 'string' && (
                        <Typography variant="caption" color="error">{errors.IsHalal}</Typography>
                      )}
                    </FormControl>
                  </Box>
                </Box>
                {/* Type of Restaurant */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Type of Restaurant <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <FormControl error={touched.restaurantType && Boolean(errors.restaurantType)} fullWidth>
                      <Box sx={{ display: 'flex', gap: 4 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.restaurantType.includes('veg')}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                let newArr = [...values.restaurantType];
                                if (checked) {
                                  newArr.push('veg');
                                } else {
                                  newArr = newArr.filter((v: string) => v !== 'veg');
                                }
                                setFieldValue('restaurantType', newArr);
                                setFieldTouched('restaurantType', true, false);
                              }}
                            />
                          }
                          label={<Typography variant="body2">veg</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.restaurantType.includes('non-veg')}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                let newArr = [...values.restaurantType];
                                if (checked) {
                                  newArr.push('non-veg');
                                } else {
                                  newArr = newArr.filter((v: string) => v !== 'non-veg');
                                }
                                setFieldValue('restaurantType', newArr);
                                setFieldTouched('restaurantType', true);
                              }}
                            />
                          }
                          label={<Typography variant="body2">non-veg</Typography>}
                        />
                      </Box>
                    </FormControl>
                    {touched.restaurantType && typeof errors.restaurantType === 'string' && (
                      <Typography variant="caption" color="error">{errors.restaurantType}</Typography>
                    )}
                  </Box>
                </Box>
                {/* Preferable Time */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Preferable Time <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <FormControl error={touched.preferableTime && Boolean(errors.preferableTime)} fullWidth>
                      <Box sx={{ display: 'flex', gap: 4 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.preferableTime.includes('Breakfast')}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                let newArr = [...values.preferableTime];
                                if (checked) {
                                  newArr.push('Breakfast');
                                } else {
                                  newArr = newArr.filter((v: string) => v !== 'Breakfast');
                                }
                                setFieldValue('preferableTime', newArr);
                                setFieldTouched('preferableTime', true, false);
                              }}
                            />
                          }
                          label={<Typography variant="body2">Breakfast</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.preferableTime.includes('Lunch')}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                let newArr = [...values.preferableTime];
                                if (checked) {
                                  newArr.push('Lunch');
                                } else {
                                  newArr = newArr.filter((v: string) => v !== 'Lunch');
                                }
                                setFieldValue('preferableTime', newArr);
                                setFieldTouched('preferableTime', true);
                              }}
                            />
                          }
                          label={<Typography variant="body2">Lunch</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.preferableTime.includes('Dinner')}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                let newArr = [...values.preferableTime];
                                if (checked) {
                                  newArr.push('Dinner');
                                } else {
                                  newArr = newArr.filter((v: string) => v !== 'Dinner');
                                }
                                setFieldValue('preferableTime', newArr);
                                setFieldTouched('preferableTime', true);
                              }}
                            />
                          }
                          label={<Typography variant="body2">Dinner</Typography>}
                        />
                      </Box>
                    </FormControl>
                    {touched.preferableTime && typeof errors.preferableTime === 'string' && (
                      <Typography variant="caption" color="error">{errors.preferableTime}</Typography>
                    )}
                  </Box>
                </Box>
                {/* Self Pickup */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Self Pickup <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                  <Box flex={1} width="100%">
                    <FormControl error={touched.hasSelfPickup && Boolean(errors.hasSelfPickup)} fullWidth>
                      <RadioGroup
                        row
                        name="hasSelfPickup"
                        value={values.hasSelfPickup}
                        onChange={(e) => setFieldValue('hasSelfPickup', e.target.value)}
                      >
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                      {touched.hasSelfPickup && typeof errors.hasSelfPickup === 'string' && (
                        <Typography variant="caption" color="error" sx={{ mt: 2, display: 'block' }}>{errors.hasSelfPickup}</Typography>
                      )}
                    </FormControl>
                  </Box>
                </Box>
                {/* Delivery Available */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                  <Typography variant="subtitle2">Delivery Available <span style={{ color: 'red' }}>*</span></Typography>
                </Box>
                  <Box flex={1} width="100%">
                    <FormControl error={touched.deliveryAvailable && Boolean(errors.deliveryAvailable)} fullWidth>
                      <RadioGroup
                        row
                        name="deliveryAvailable"
                        value={values.deliveryAvailable}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFieldValue('deliveryAvailable', val);
                          if (val === 'No') {
                            setFieldValue('deliveryTime', '');
                            setFieldValue('deliveryTimeUnit', '');
                          }
                        }}
                      >
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                      {touched.deliveryAvailable && typeof errors.deliveryAvailable === 'string' && (
                        <Typography variant="caption" color="error" sx={{ mt: 4, display: 'block' }}>{errors.deliveryAvailable}</Typography>
                      )}
                    </FormControl>
                  </Box>
                </Box>
              {/* Delivery fee settings now always use the platform default configuration,
                  so we no longer show a dedicated delivery fee section here. */}
                {/* Response Time and Unit (combined row) */}
                {values.deliveryAvailable === 'Yes' && (
                  <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                    <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                      <Typography variant="subtitle2">Response Time <span style={{ color: 'red' }}>*</span></Typography>
                    </Box>
                    <Box flex={1} width="100%">
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: { xs: '100%', sm: '400px' } }}>
                        <FormControl error={touched.deliveryTime && Boolean(errors.deliveryTime)} sx={{ flex: 1 }}>
                          <OutlinedInput
                            name="deliveryTime"
                            value={values.deliveryTime}
                            onChange={(e) => setFieldValue('deliveryTime', e.target.value)}
                            onBlur={() => setFieldTouched('deliveryTime', true)}
                            onKeyDown={(e) => {
                              const isDigit = /^[0-9]$/.test(e.key);
                              const allowedKeys = [
                                'Backspace',
                                'Delete',
                                'Tab',
                                'ArrowLeft',
                                'ArrowRight',
                              ];
                              if (!isDigit && !allowedKeys.includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            placeholder="Enter Time"
                          />
                        </FormControl>
                        <FormControl error={touched.deliveryTimeUnit && Boolean(errors.deliveryTimeUnit)} sx={{ minWidth: 120 }}>
                          <Select
                            value={values.deliveryTimeUnit}
                            onChange={(e) => setFieldValue('deliveryTimeUnit', e.target.value)}
                            displayEmpty
                            input={<OutlinedInput />}
                          >
                            <MenuItem value="">
                              <em>Select Unit</em>
                            </MenuItem>
                            <MenuItem value="Minutes">Minutes</MenuItem>
                            <MenuItem value="Hours">Hours</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                      {(touched.deliveryTime && typeof errors.deliveryTime === 'string') && (
                        <Typography variant="caption" color="error">{errors.deliveryTime}</Typography>
                      )}
                      {(touched.deliveryTimeUnit && typeof errors.deliveryTimeUnit === 'string') && (
                        <Typography variant="caption" color="error">{errors.deliveryTimeUnit}</Typography>
                      )}
                    </Box>
                  </Box>
                )}
                {/* Additional Cutlery Request */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Additional Cutlery Request <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <FormControl error={touched.additionalCulinaryRequest && Boolean(errors.additionalCulinaryRequest)} fullWidth>
                      <RadioGroup
                        row
                        name="additionalCulinaryRequest"
                        value={values.additionalCulinaryRequest}
                        onChange={(e) => setFieldValue('additionalCulinaryRequest', e.target.value)}
                      >
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>
                    {touched.additionalCulinaryRequest && typeof errors.additionalCulinaryRequest === 'string' && (
                      <Typography variant="caption" color="error">{errors.additionalCulinaryRequest}</Typography>
                    )}
                  </Box>
                </Box>
                {/* About Store */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">About Store <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <MyEditor
                      value={values.aboutStore}
                      onChange={(val: string) => setFieldValue('aboutStore', val)}
                      onBlur={() => setFieldTouched('aboutStore', true)}
                    />
                    {((touched.aboutStore || submitCount > 0) && typeof errors.aboutStore === 'string') && (
                      <Typography variant="caption" color="error">{errors.aboutStore}</Typography>
                    )}
                  </Box>
                </Box>
                {/* About Store Arabic */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">About Store (Arabic) <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <MyEditor
                      value={values.aboutStoreArabic}
                      onChange={(val: string) => setFieldValue('aboutStoreArabic', val)}
                      onBlur={() => setFieldTouched('aboutStoreArabic', true)}
                    />
                    {((touched.aboutStoreArabic || submitCount > 0) && typeof errors.aboutStoreArabic === 'string') && (
                      <Typography variant="caption" color="error">{errors.aboutStoreArabic}</Typography>
                    )}
                  </Box>
                </Box>
                {/* Delivery Radius */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Delivery Radius (KM) <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <FormControl error={touched.deliveryRadiusKM && Boolean(errors.deliveryRadiusKM)} fullWidth>
                      <OutlinedInput
                        name="deliveryRadiusKM"
                        value={values.deliveryRadiusKM}
                        onChange={(e) => setFieldValue('deliveryRadiusKM', e.target.value)}
                        onBlur={() => setFieldTouched('deliveryRadiusKM', true)}
                        onKeyDown={(e) => {
                          const isDigit = /^[0-9]$/.test(e.key);
                          const allowedKeys = [
                            'Backspace',
                            'Delete',
                            'Tab',
                            'ArrowLeft',
                            'ArrowRight',
                          ];
                          if (!isDigit && !allowedKeys.includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        placeholder="Enter Delivery Radius"
                        sx={{ width: { xs: '100%', sm: '400px' } }}
                      />

                    </FormControl>
                    {touched.deliveryRadiusKM && typeof errors.deliveryRadiusKM === 'string' && (
                      <Typography variant="caption" color="error">{errors.deliveryRadiusKM}</Typography>
                    )}
                  </Box>
                </Box>
                {/* Store Phone Number */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Store Phone Number <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <Field
                      name="storePhoneNumber"
                      component={StorePhoneNumberField}
                      selectedCountryCode={selectedCountryCode}
                      onCountryCodeChange={setSelectedCountryCode}
                    />
                  </Box>
                </Box>
                {/* Store Address */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Store Address <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    {/* <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Store Address</Typography> */}
                    <LocationComponent
                      field={{ name: 'storeAddress', value: values.storeAddress }}
                      form={{ values, errors, touched, setFieldValue, setFieldTouched }}
                    />
                  </Box>
                </Box>
                {/* Store Logo */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Store Logo <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <Box mt={2}>
                      <ImageUploadField name="storeLogo" label="Store Logo" />
                      {(touched.storeLogo || values.storeLogo) && typeof errors.storeLogo === 'string' && (
                        <Typography variant="caption" color="error">{errors.storeLogo}</Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                {/* Store Banner */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">banner <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <Box mt={2}>
                      <ImageUploadField name="storeBanner" label="banner" multiple maxFiles={6} recommendedText="Max 6 images" />
                      {(touched.storeBanner || (values.storeBanner && values.storeBanner.length > 0)) && typeof errors.storeBanner === 'string' && (
                        <Typography variant="caption" color="error">{errors.storeBanner}</Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                {/* Item Available Time */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Item Available Time <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <FormControl fullWidth>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {(['breakfast', 'lunch', 'dinner'] as const)
                          .filter((mealKey) => {
                            const mapping: Record<string, string> = {
                              breakfast: 'Breakfast',
                              lunch: 'Lunch',
                              dinner: 'Dinner',
                            };
                            return values.preferableTime.includes(mapping[mealKey]);
                          })
                          .map((mealKey) => {
                          const mealErrors = (errors?.itemAvailableTime && (errors.itemAvailableTime as any)[mealKey]) || {};
                          const mealTouched = (touched?.itemAvailableTime && (touched.itemAvailableTime as any)[mealKey]) || {};
                          return (
                            <Box
                              key={mealKey}
                              sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Box sx={{ minWidth: { xs: 80, sm: 100 }, maxWidth: { xs: 120, sm: 140 }, mr: 1, display: 'flex', alignItems: 'center' }}>
                                <Typography sx={{ color: 'text.secondary', fontSize: 14, whiteSpace: 'nowrap' }}>{mealKey.charAt(0).toUpperCase() + mealKey.slice(1)}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                {/* Start Time */}
                                <FormControl variant="outlined" size="small" sx={{ minWidth: 120, mr: 2 }} error={Boolean(mealTouched?.startTime && mealErrors?.startTime)}>
                                  <Select
                                    value={values.itemAvailableTime[mealKey]?.startTime?.replace(':00Z', '') || ''}
                                    onChange={(e) => {
                                      setFieldValue(`itemAvailableTime.${mealKey}.startTime`, `${e.target.value}:00Z`);
                                      setFieldTouched(`itemAvailableTime.${mealKey}.startTime`, true, false);
                                    }}
                                    onBlur={() => setFieldTouched(`itemAvailableTime.${mealKey}.startTime`, true, false)}
                                    displayEmpty
                                  >
                                    <MenuItem value="">
                                      <em>Select Start</em>
                                    </MenuItem>
                                    {generateTimeOptions().map((time) => (
                                      <MenuItem key={time} value={time}>{time}</MenuItem>
                                    ))}
                                  </Select>
                                  {mealTouched?.startTime && typeof mealErrors?.startTime === 'string' && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{mealErrors.startTime}</Typography>
                                  )}
                                </FormControl>
                                {/* End Time */}
                                <FormControl variant="outlined" size="small" sx={{ minWidth: 120, mr: 2 }} error={Boolean(mealTouched?.endTime && mealErrors?.endTime)}>
                                  <Select
                                    value={values.itemAvailableTime[mealKey]?.endTime?.replace(':00Z', '') || ''}
                                    onChange={(e) => {
                                      setFieldValue(`itemAvailableTime.${mealKey}.endTime`, `${e.target.value}:00Z`);
                                      setFieldTouched(`itemAvailableTime.${mealKey}.endTime`, true, false);
                                    }}
                                    onBlur={() => setFieldTouched(`itemAvailableTime.${mealKey}.endTime`, true, false)}
                                    displayEmpty
                                  >
                                    <MenuItem value="">
                                      <em>Select End</em>
                                    </MenuItem>
                                    {generateTimeOptions().map((time) => (
                                      <MenuItem key={time} value={time}>{time}</MenuItem>
                                    ))}
                                  </Select>
                                  {mealTouched?.endTime && typeof mealErrors?.endTime === 'string' && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{mealErrors.endTime}</Typography>
                                  )}
                                </FormControl>
                              </Box>
                            </Box>
                          );
                        })}
                        {/* Overlap error */}
                        {touched?.itemAvailableTime && typeof errors?.itemAvailableTime === 'string' && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors.itemAvailableTime}</Typography>
                        )}
                      </Box>
                    </FormControl>
                  </Box>
                </Box>
                {/* Working Hours */}
                <Box display="flex" alignItems="center" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <Box minWidth={180} sx={{ mb: { xs: 0.5, sm: 0 } }}>
                    <Typography variant="subtitle2">Working Hours <span style={{ color: 'red' }}>*</span></Typography>
                  </Box>
                  <Box flex={1} width="100%">
                    <FormControl fullWidth>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        {(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const).map((dayKey) => {
                          const dayData = values.workingHours[dayKey];
                          const dayErrors = (errors?.workingHours && (errors.workingHours as any)[dayKey]) || {};
                          const dayTouched = (touched?.workingHours && (touched.workingHours as any)[dayKey]) || {};
                          return (
                            <Box key={dayKey} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Box sx={{ minWidth: { xs: 80, sm: 100 }, maxWidth: { xs: 120, sm: 140 }, mr: 1, display: 'flex', alignItems: 'center' }}>
                                <Typography sx={{ color: 'text.secondary', fontSize: 14, whiteSpace: 'nowrap' }}>{dayKey.charAt(0).toUpperCase() + dayKey.slice(1)} *</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={!dayData.isOpen}
                                      onChange={(e) => {
                                        setFieldValue(`workingHours.${dayKey}.isOpen`, !e.target.checked);
                                        if (e.target.checked) {
                                          setFieldValue(`workingHours.${dayKey}.startTime`, '00:00:00Z');
                                          setFieldValue(`workingHours.${dayKey}.endTime`, '00:00:00Z');
                                        }
                                        setFieldTouched(`workingHours.${dayKey}`, true, false);
                                      }}
                                      size="small"
                                    />
                                  }
                                  label={<Typography variant="body2" color="text.secondary">Closed</Typography>}
                                />
                                {dayData.isOpen && (
                                  <Box sx={{ display: 'flex', gap: 4 }}>
                                    {/* Start Time Select */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                      <FormControl error={Boolean(dayTouched?.startTime && dayErrors?.startTime)} size="small" variant="outlined" sx={{ minWidth: 120, mr: 2 }}>
                                        <Select
                                          value={dayData.startTime ? dayData.startTime.replace(':00Z', '') : ''}
                                          onChange={(e) => {
                                            setFieldValue(`workingHours.${dayKey}.startTime`, e.target.value ? `${e.target.value}:00Z` : '00:00:00Z');
                                            setFieldTouched(`workingHours.${dayKey}.startTime`, true, false);
                                          }}
                                          onBlur={() => setFieldTouched(`workingHours.${dayKey}.startTime`, true, false)}
                                          displayEmpty
                                        >
                                          <MenuItem value="">
                                            <em>Select Start</em>
                                          </MenuItem>
                                          {generateTimeOptions().map((time) => (
                                            <MenuItem key={time} value={time}>{time}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                      {dayTouched?.startTime && typeof dayErrors?.startTime === 'string' && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{dayErrors.startTime}</Typography>
                                      )}
                                    </Box>
                                    {/* End Time Select */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                      <FormControl error={Boolean(dayTouched?.endTime && dayErrors?.endTime)} size="small" variant="outlined" sx={{ minWidth: 120, mr: 2 }}>
                                        <Select
                                          value={dayData.endTime ? dayData.endTime.replace(':00Z', '') : ''}
                                          onChange={(e) => {
                                            setFieldValue(`workingHours.${dayKey}.endTime`, e.target.value ? `${e.target.value}:00Z` : '00:00:00Z');
                                            setFieldTouched(`workingHours.${dayKey}.endTime`, true, false);
                                          }}
                                          onBlur={() => setFieldTouched(`workingHours.${dayKey}.endTime`, true, false)}
                                          displayEmpty
                                        >
                                          <MenuItem value="">
                                            <em>Select End</em>
                                          </MenuItem>
                                          {generateTimeOptions().map((time) => (
                                            <MenuItem key={time} value={time}>{time}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                      {dayTouched?.endTime && typeof dayErrors?.endTime === 'string' && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{dayErrors.endTime}</Typography>
                                      )}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </FormControl>
                  </Box>
                </Box>
                {/* Buttons */}
                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', justifyContent: 'center', width: '100%', mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{ textTransform: 'none', fontSize: 14, px: 3, py: 1, backgroundColor: COLORS.primary.main, '&:hover': { backgroundColor: alpha(COLORS.primary.main, 0.8) }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (<><CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />Updating...</>) : ('Update')}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    color="error"
                    onClick={onCancel}
                    sx={{ bgcolor: warning.main, color: '#fff', '&:hover': { bgcolor: warning.dark, color: '#fff' } }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Form>
            )
          }

          }
        </Formik>
      </Box>
    </>
  );
};

export default EditRestaurant;
